import React from 'react';
import { Divider, Grid, useMediaQuery } from 'housecall-ui';
import theme from 'housecall-ui/dist/Theme';
import moment from 'moment';

import { formatMoney } from 'components/OnlineBookingSettings/utils/formatMoney';

import { useOrgData } from '../hooks/useOrgData';
import { State } from '../state/types';
import { useOnlineBookingReducer } from '../state/useOnlineBookingReducer';

import ConfirmationElement from './ConfirmationElement';

interface ConfirmationCardType {
  title: string;
  content: string;
  showDividerAfter?: boolean;
  summary?: string;
}

const prepareCards = (
  { service, description, contactDetails, arrivalWindow, question1, question2 }: State,
  isExactTime: boolean,
  timezone?: string
) => {
  const price = formatMoney(service?.price ?? 0);
  const total = formatMoney(service?.totalPrice ?? 0);
  const tax = service?.taxInfo;
  const { firstName, lastName, phone, email, addressLine1, addressLine2, city, state, zipCode } = contactDetails.values;

  return [
    {
      title: 'Selected services',
      content: `${service?.name}
      ${
        tax
          ? `Subtotal: ${price}
      Tax, ${tax.name} (${tax.rate * 100}%): ${formatMoney(service.totalPrice - service.price)}`
          : ''
      }`,
      summary: `Total: ${total}`,
    },
    {
      title: 'Arrival time',
      content: `Arriving on ${moment(arrivalWindow.date).format('dddd, MMM Do')}
      ${
        isExactTime
          ? `At ${arrivalWindow?.time?.from}  ${timezone ? moment().tz(timezone).zoneAbbr() : ''}`
          : `Between ${arrivalWindow?.time?.from} - ${arrivalWindow?.time?.to} ${
              timezone ? moment().tz(timezone).zoneAbbr() : ''
            }`
      } `,
    },
    description && {
      title: 'Your requirements',
      content: description,
    },
    {
      title: 'Location',
      content: `${addressLine2} ${addressLine1}, ${city}, ${state} ${zipCode}`,
    },
    question1 &&
      service?.question1 && {
        title: service?.question1,
        content: question1,
      },
    question2 &&
      service?.question2 && {
        title: service?.question2,
        content: question2,
      },
    {
      title: 'Your contact details',
      content: `${firstName} ${lastName}
      ${phone}
      ${email}`,
    },
  ].filter((cardInfo) => !!cardInfo) as ConfirmationCardType[];
};

const ConfirmationElementsList = () => {
  const { state } = useOnlineBookingReducer();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const { data } = useOrgData({ orgUuid: state.orgUuid });

  const isExactTime = data?.default_arrival_window === 0;

  const confirmationCards = prepareCards(state, isExactTime, data?.time_zone).map((card, index, cardsArray) => {
    const notLast = index + 1 !== cardsArray.length;
    const isEven = (index + 1) % 2 === 0;
    return {
      showDividerAfter: fullScreen ? notLast : notLast && isEven,
      ...card,
    };
  });

  return (
    <Grid container spacing={2}>
      {confirmationCards.map((cardInfo) => (
        <React.Fragment key={cardInfo.title}>
          <ConfirmationElement
            key={cardInfo.title}
            title={cardInfo.title}
            subtitle={cardInfo.content}
            summary={cardInfo.summary}
          />
          {cardInfo.showDividerAfter && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}
        </React.Fragment>
      ))}
    </Grid>
  );
};

export default ConfirmationElementsList;
